import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import Drawer from '@mui/material/Drawer';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as ArrowLeft } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import { ReactComponent as BulletPoints } from 'src/Images/icons/common/bulletpoints.svg';
import { ReactComponent as DocumentIcon } from 'src/Images/icons/planning/Document.svg';
import { ReactComponent as PackQuantityIcon } from 'src/Images/icons/planning/PackQuantity.svg';
import { ReactComponent as DeliveryQuantityIcon } from 'src/Images/icons/planning/DeliveryQuantity.svg';
import CreateDeliveryEmptyState from 'src/Images/icons/vehicle-tracking/create-delivery-empty-state.svg';
import TableRightDrawer from 'src/components/TableRightDrawer';
import {
  getDeliveryForBusinessPartner,
  getDeliveryDropdownOptions,
  getCategoryBusinessPartnersDropdownOptions,
} from 'src/redux/Actions/VehicleTrackingOutward';
import 'src/style/createVehicleTracking.css';

function DeliverySummaryCard({ Icon, label, value, uom, themeColor }) {
  const displayValue =
    typeof value === 'number' && uom !== 'Total' ? value.toFixed(2) : value;

  return (
    <div className="planning-dashboard-card div-flex-row div-space-between">
      <div className="div-flex-column rg-5 div-align-start">
        <span className="text-md text-semi-bold text-nowrap">{label}</span>
        <div className="div-flex-row cg-5 div-align-baseline">
          <span className="text-xxl text-bold">{displayValue}</span>
          <span className="text-md text-grey">{uom}</span>
        </div>
      </div>
      <div className="div-flex-column div-align-end">
        <span
          className={`delivery-summary-icon theme-text-color-${themeColor}`}
        >
          <Icon className="icon-size-xxxl" />
        </span>
      </div>
    </div>
  );
}

const getDefaultDeliveryFilters = () => ({
  deliveryOrderSearchedTerm: null,
  businessPartnerSearchTerm: '',
  deliveryStartDate: new Date().toISOString().split('T')[0],
  deliveryEndDate: new Date().toISOString().split('T')[0],
});

function OutwardVehicleTrackingAttachDeliverySection({
  formData,
  selectedDeliveryOrder,
  setSelectedDeliveryOrder,
  handleOnChange,
  selectedBusinessPartner,
  setSelectedBusinessPartner,
  deliveryFilters,
  setDeliveryFilters,
}) {
  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.AuthReducer);
  const VehicleTrackingOutwardReducer = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );

  const themeColor = authReducer?.userData?.tenant?.theme;
  const tenantOrganisationId = authReducer.userData.organisations?.id;

  const selectedDeliveryResponse =
    VehicleTrackingOutwardReducer?.deliveryLineitemData?.delivery != null
      ? VehicleTrackingOutwardReducer?.deliveryLineitemData
      : VehicleTrackingOutwardReducer?.deliveryDropdownOptions;

  const deliveryLineitemData = selectedDeliveryResponse?.delivery || [];

  const fieldFilter = selectedDeliveryResponse?.fieldMapping || [];

  const totalPages = selectedDeliveryResponse?.totalPages;

  const { categoryBasedBusinessPartner } = VehicleTrackingOutwardReducer;

  // Filter state can live in CreateVehicleTrackingPlanningForOutward so it
  // survives switching between the Basic Details / Attach Delivery / Attach
  // Load tabs. Other callers continue to use local filter state.
  const [localDeliveryFilters, setLocalDeliveryFilters] = useState(
    getDefaultDeliveryFilters,
  );
  const shouldUseProvidedFilters = Boolean(
    deliveryFilters && setDeliveryFilters,
  );
  const activeDeliveryFilters = shouldUseProvidedFilters
    ? deliveryFilters
    : localDeliveryFilters;
  const updateDeliveryFilters = shouldUseProvidedFilters
    ? setDeliveryFilters
    : setLocalDeliveryFilters;

  const {
    deliveryOrderSearchedTerm,
    businessPartnerSearchTerm,
    deliveryStartDate,
    deliveryEndDate,
  } = activeDeliveryFilters;

  const setDeliveryOrderSearchedTerm = (value) =>
    updateDeliveryFilters((prev) => ({
      ...prev,
      deliveryOrderSearchedTerm: value,
    }));
  const setBusinessPartnerSearchTerm = (value) =>
    updateDeliveryFilters((prev) => ({
      ...prev,
      businessPartnerSearchTerm: value,
    }));
  const setDeliveryStartDate = (value) =>
    updateDeliveryFilters((prev) => ({ ...prev, deliveryStartDate: value }));
  const setDeliveryEndDate = (value) =>
    updateDeliveryFilters((prev) => ({ ...prev, deliveryEndDate: value }));

  const [pageNumber, setPageNumber] = useState(1);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [activePackTypeSummaryKey, setActivePackTypeSummaryKey] =
    useState(null);

  const DELIVERY_TABLE_PREFERENCE_KEY = 'savedOutwardDeliveryColumns';

  const loadDeliveryTablePreferences = () => {
    const saved = localStorage.getItem(DELIVERY_TABLE_PREFERENCE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          order: parsed.order || [],
          selected: parsed.selected || [],
        };
      } catch (e) {
        return {
          order: [],
          selected: [],
        };
      }
    }
    return {
      order: [],
      selected: [],
    };
  };

  const { order: initialOrder, selected: initialSelected } =
    loadDeliveryTablePreferences();

  const [draggedFieldOrder, setDraggedFieldOrder] = useState(initialOrder);
  const [selectedFieldFilter, setSelectedFieldFilter] =
    useState(initialSelected);

  useEffect(() => {
    if (formData?.showDeliveryComponent && formData?.vehicleNumber) {
      dispatch(
        getDeliveryDropdownOptions(
          tenantOrganisationId,
          formData.vehicleNumber,
          pageNumber,
          null,
          deliveryStartDate,
          deliveryEndDate,
        ),
      );
    }
  }, [
    formData?.showDeliveryComponent,
    formData?.vehicleNumber,
    deliveryStartDate,
    deliveryEndDate,
  ]);

  useEffect(() => {
    // 1 for Customer
    dispatch(
      getCategoryBusinessPartnersDropdownOptions(
        tenantOrganisationId,
        businessPartnerSearchTerm,
        '1',
      ),
    );
  }, [tenantOrganisationId, businessPartnerSearchTerm]);

  useEffect(() => {
    if (tenantOrganisationId && selectedBusinessPartner) {
      dispatch(
        getDeliveryForBusinessPartner({
          search_term: deliveryOrderSearchedTerm,
          business_partner_id: selectedBusinessPartner,
          tenant_organisation_id: tenantOrganisationId,
          page_number: pageNumber,
        }),
      );
    }
  }, [deliveryOrderSearchedTerm, selectedBusinessPartner, pageNumber]);

  useEffect(() => {
    if (fieldFilter.length) {
      const fieldValues = fieldFilter.map((field) => field.value);
      const validSelectedFields = selectedFieldFilter.filter((fieldValue) =>
        fieldValues.includes(fieldValue),
      );
      const missingFields = fieldValues.filter(
        (fieldValue) => !validSelectedFields.includes(fieldValue),
      );

      if (
        selectedFieldFilter.length === 0 ||
        validSelectedFields.length !== selectedFieldFilter.length
      ) {
        setSelectedFieldFilter([...validSelectedFields, ...missingFields]);
      }
    }
  }, [fieldFilter, selectedFieldFilter]);

  useEffect(() => {
    localStorage.setItem(
      DELIVERY_TABLE_PREFERENCE_KEY,
      JSON.stringify({
        order: draggedFieldOrder,
        selected: selectedFieldFilter,
      }),
    );
  }, [draggedFieldOrder, selectedFieldFilter]);

  const businessPartnersWithDeliveryOptions = categoryBasedBusinessPartner.map(
    (transporterObj) => ({
      value: transporterObj.id,
      label: transporterObj.name,
    }),
  );

  const handleBusinessPartnerSelectChange = (event) => {
    setSelectedBusinessPartner(event.target.value);
  };

  const handlePurchaseOrderSearchSubmit = () => {
    const payload = {
      search_term: deliveryOrderSearchedTerm,
      tenant_organisation_id: tenantOrganisationId,
      page_number: pageNumber,
    };
    if (selectedBusinessPartner) {
      payload.business_partner_id = selectedBusinessPartner;
    }

    if (tenantOrganisationId && deliveryOrderSearchedTerm) {
      dispatch(getDeliveryForBusinessPartner(payload));
    }
  };

  const toggleDrawer = (open) => () => {
    setOpenDrawer(open);
  };

  const handleSelectedFieldFilter = (e, value) => {
    let updated = [...selectedFieldFilter];
    if (e.target.checked) {
      updated.push(value);
    } else {
      updated = updated.filter((fieldValue) => fieldValue !== value);
    }

    setSelectedFieldFilter(updated);
  };

  let filteredFieldFilter = [];

  if (Array.isArray(fieldFilter)) {
    const source =
      draggedFieldOrder?.length > 0 ? draggedFieldOrder : selectedFieldFilter;

    filteredFieldFilter = source
      .map((value) => fieldFilter?.find((field) => field.value === value))
      .filter(Boolean);
  }

  const deliveryLevelFields = [
    'delivery_code',
    'delivery_unique_number',
    'sap_code',
    'delivery_date',
    'ship_to_party',
    'total_quantity',
    'pack_type',
    'pack_quantity',
    'grade',
    'weight',
  ];

  const getUnitOfMeasurement = (item) => item?.unit_of_measurement?.name;

  const formatValueWithUom = (value, item) => {
    const uom = getUnitOfMeasurement(item);
    const displayValue =
      value !== null && value !== undefined && value !== '' ? value : '-';

    return uom && displayValue !== '-'
      ? `${displayValue} ${uom}`
      : displayValue;
  };

  const deliverySelectionSummary = useMemo(() => {
    const deliveries = selectedDeliveryOrder || [];
    const packTypeSummaryMap = {};

    deliveries.forEach((item) => {
      const packTypeKey = item.pack_type ?? 'unassigned';
      const packTypeName = item.pack_type ?? 'Unassigned';

      if (!packTypeSummaryMap[packTypeKey]) {
        packTypeSummaryMap[packTypeKey] = {
          summaryKey: `pack-type-${packTypeKey}`,
          packTypeName,
          totalDocuments: 0,
          totalPackQuantity: 0,
          totalDeliveryQuantity: 0,
          totalGrossWeight: 0,
          quantityUOM: item?.line_items?.[0]?.unit_of_measurement?.name,
        };
      }

      packTypeSummaryMap[packTypeKey].totalDocuments += 1;
      packTypeSummaryMap[packTypeKey].totalPackQuantity += Number(
        item.pack_quantity || 0,
      );
      packTypeSummaryMap[packTypeKey].totalDeliveryQuantity += Number(
        item.total_quantity || 0,
      );
      packTypeSummaryMap[packTypeKey].totalGrossWeight += Number(
        item.weight || 0,
      );
    });

    return Object.values(packTypeSummaryMap);
  }, [selectedDeliveryOrder]);

  const activePackTypeSummary =
    deliverySelectionSummary.find(
      (summary) => summary.summaryKey === activePackTypeSummaryKey,
    ) || deliverySelectionSummary[0];

  const activeSummaryCards = [
    {
      label: 'Total Documents',
      key: 'total-documents',
      value: activePackTypeSummary?.totalDocuments,
      uom: 'Nos',
      Icon: DocumentIcon,
    },
    {
      label: 'Total Pack Quantity',
      key: 'total-pack-quantity',
      value: activePackTypeSummary?.totalPackQuantity,
      uom: 'Nos',
      Icon: PackQuantityIcon,
    },
    {
      label: 'Total Delivery Quantity',
      key: 'total-delivery-quantity',
      value: activePackTypeSummary?.totalDeliveryQuantity,
      uom: activePackTypeSummary?.quantityUOM,
      Icon: DeliveryQuantityIcon,
    },
    {
      label: 'Gross Weight',
      key: 'gross-weight',
      value: activePackTypeSummary?.totalGrossWeight,
      uom: activePackTypeSummary?.quantityUOM,
      Icon: DeliveryQuantityIcon,
    },
  ];

  const getDeliveryColumnValue = (fieldKey, deliveryObj, item) => {
    switch (fieldKey) {
      case 'ship_to_party':
        return deliveryObj?.ship_to_party?.name;
      case 'weight':
        return formatValueWithUom(deliveryObj?.weight, item);
      case 'pack_type':
        return deliveryObj?.pack_type;
      case 'product_name':
        return item?.product?.product_name;
      case 'product_sap_code':
        return item?.product?.product_sap_code;
      case 'product_description':
        return item?.product_description || item?.product?.product_description;
      case 'plant':
        return item?.plant?.name;
      case 'quantity':
        return formatValueWithUom(item?.quantity, item);
      default:
        return item?.[fieldKey] ?? deliveryObj?.[fieldKey];
    }
  };

  const renderDeliveryColumnValue = (fieldKey, deliveryObj, item) => {
    const value = getDeliveryColumnValue(fieldKey, deliveryObj, item);
    return value !== null && value !== undefined && value !== '' ? value : '-';
  };

  const handleDeliverySelection = (e, deliveryObj) => {
    if (e.target.checked) {
      // Add if checked
      setSelectedDeliveryOrder([...selectedDeliveryOrder, deliveryObj]);
    } else {
      // Remove by filtering out the object with matching delivery_id
      setSelectedDeliveryOrder(
        selectedDeliveryOrder.filter(
          (item) => item.delivery_id !== deliveryObj.delivery_id,
        ),
      );
    }
  };

  const isAllDeliveriesSelected =
    (deliveryLineitemData || []).length > 0 &&
    deliveryLineitemData.every((deliveryObj) =>
      selectedDeliveryOrder.some(
        (item) => item.delivery_id === deliveryObj.delivery_id,
      ),
    );

  const handleSelectAllDeliveries = (e) => {
    if (e.target.checked) {
      const newSelections = deliveryLineitemData.filter(
        (deliveryObj) =>
          !selectedDeliveryOrder.some(
            (item) => item.delivery_id === deliveryObj.delivery_id,
          ),
      );
      setSelectedDeliveryOrder([...selectedDeliveryOrder, ...newSelections]);
    } else {
      setSelectedDeliveryOrder(
        selectedDeliveryOrder.filter(
          (item) =>
            !deliveryLineitemData.some(
              (deliveryObj) => deliveryObj.delivery_id === item.delivery_id,
            ),
        ),
      );
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const handleInputChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
      setPageNumber(value);
    }
  };

  const formattedPageNumber = pageNumber < 10 ? `0${pageNumber}` : pageNumber;
  return (
    <div className="div-flex-column container-card-shadow border-radius-4 min-height-20vh margin-top-10">
      <div className="container-padding-5 ">
        {formData?.showDeliveryComponent ? (
          <div className="container-padding-10">
            <div className="div-flex-column">
              <div className="div-flex-column rg-10 vertical-padding-10">
                {deliverySelectionSummary.length > 0 && (
                  <>
                    <div className="div-flex-row cg-5 border-bottom padding-bottom-8">
                      {deliverySelectionSummary.map((packTypeSummary) => {
                        const isActive =
                          packTypeSummary.summaryKey ===
                          activePackTypeSummary?.summaryKey;

                        return (
                          <button
                            key={packTypeSummary.summaryKey}
                            type="button"
                            className={`div-flex-row div-align-center text-sm ${
                              isActive
                                ? `theme-pill-active-${themeColor}`
                                : 'border border-radius-4 vertical-padding-5 horizontal-padding-10 text-grey'
                            }`}
                            onClick={() =>
                              setActivePackTypeSummaryKey(
                                packTypeSummary.summaryKey,
                              )
                            }
                          >
                            <span>{packTypeSummary.packTypeName}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="div-flex-row cg-5 delivery-selection-summary-row">
                      {activeSummaryCards.map((summaryCard) => (
                        <div
                          key={summaryCard.key}
                          className="delivery-selection-summary-item"
                        >
                          <DeliverySummaryCard
                            Icon={summaryCard.Icon}
                            label={summaryCard.label}
                            value={summaryCard.value}
                            uom={summaryCard.uom}
                            themeColor={themeColor}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <span className="text-xxl text-bold">Select Delivery</span>
              <Row>
                <Col
                  xxl={3}
                  xl={3}
                  lg={3}
                  md={6}
                  sm={12}
                  xs={12}
                  className="form-input-container"
                >
                  <div className="div-flex-column">
                    <span className="text-sm text-bold">Delivery Number</span>
                    <div className="input-wrapper">
                      <input
                        className="form-input-section margin-top-5"
                        type="text"
                        placeholder="Search Delivery Number"
                        name="search"
                        value={deliveryOrderSearchedTerm || ''}
                        onChange={(e) =>
                          setDeliveryOrderSearchedTerm(e.target.value)
                        }
                      />
                      <span className="form-input-section-icon">
                        <Search className="icon-size-sm" color="#9F9F9F" />
                      </span>
                    </div>
                  </div>
                </Col>

                <Col
                  xxl={3}
                  xl={3}
                  lg={3}
                  md={6}
                  sm={12}
                  xs={12}
                  className="form-input-container"
                >
                  <div className="div-flex-column">
                    <span className="text-sm text-bold">Business Partner</span>
                    <div className="input-wrapper">
                      <CustomMultiSelect
                        name="business-partner"
                        isMulti={false}
                        className="multi-select-input-section margin-top-5"
                        onChange={(selectedOption) => {
                          setBusinessPartnerSearchTerm('');
                          handleBusinessPartnerSelectChange({
                            target: {
                              name: 'business-partner',
                              value: selectedOption?.value || null,
                            },
                          });
                        }}
                        onInputChange={(inputValue, { action }) => {
                          if (action === 'input-change') {
                            setBusinessPartnerSearchTerm(inputValue);
                          }
                        }}
                        inputValue={businessPartnerSearchTerm}
                        options={businessPartnersWithDeliveryOptions}
                        classNamePrefix="react-select"
                        placeholder="Search Business Partner"
                        value={businessPartnersWithDeliveryOptions.find(
                          (option) => option.value === selectedBusinessPartner,
                        )}
                        filterOption={() => true}
                      />
                      <span className="form-input-section-icon">
                        <Search className="icon-size-sm" color="#9F9F9F" />
                      </span>
                    </div>
                  </div>
                </Col>

                <Col
                  xxl={2}
                  xl={2}
                  lg={2}
                  md={6}
                  sm={12}
                  xs={12}
                  className="form-input-container"
                >
                  <div className="div-flex-column">
                    <span className="text-sm text-bold">
                      Delivery Start Date
                    </span>

                    <input
                      type="date"
                      className="form-input-section margin-top-5"
                      value={deliveryStartDate}
                      onChange={(e) => setDeliveryStartDate(e.target.value)}
                    />
                  </div>
                </Col>

                <Col
                  xxl={2}
                  xl={2}
                  lg={2}
                  md={6}
                  sm={12}
                  xs={12}
                  className="form-input-container"
                >
                  <div className="div-flex-column">
                    <span className="text-sm text-bold">Delivery End Date</span>

                    <input
                      type="date"
                      className="form-input-section margin-top-5"
                      value={deliveryEndDate}
                      onChange={(e) => setDeliveryEndDate(e.target.value)}
                    />
                  </div>
                </Col>

                <Col
                  xxl={2}
                  xl={2}
                  lg={2}
                  md={12}
                  sm={12}
                  xs={12}
                  className="form-input-container d-flex flex-column justify-content-end"
                >
                  <button
                    type="button"
                    className={`search-purchase-order-btn theme-background-${themeColor}`}
                    onClick={handlePurchaseOrderSearchSubmit}
                  >
                    Search
                  </button>
                </Col>
              </Row>
            </div>

            {/* list of purchase order */}
            {(deliveryLineitemData || []).length === 0 ? (
              <div className="empty-state-container">
                <img
                  src={CreateDeliveryEmptyState}
                  alt="Search PO to proceed"
                  style={{
                    maxWidth: '100%',
                    width: '200px',
                    height: 'auto',
                  }}
                />
                <div className="margin-top-10">
                  <span className="text-xxl text-bold">
                    No delivery planned
                  </span>
                </div>
                <div>
                  <span className="text-md text-grey">
                    To plan your delivery at the moment click on checkbox above.{' '}
                    <br /> Add all the required delivery to add in this vehicle.
                  </span>
                </div>
              </div>
            ) : null}
            {(deliveryLineitemData || []).length > 0 ? (
              <hr className="divider-line" />
            ) : null}
            <div className="margin-top-5">
              {(deliveryLineitemData || []).length > 0 && (
                <div className="purchase-order-line-item-card border-radius-4">
                  <div className="div-space-between div-align-center px-2 py-2 border-bottom">
                    <span className="text-md text-bold delivery-header">
                      Delivery
                    </span>
                    <button
                      className="icon-btn-none delivery-organiser-btn"
                      onClick={toggleDrawer(true)}
                      aria-label="Organiser"
                      type="button"
                    >
                      <BulletPoints className="icon-size-lg" color="#9F9F9F" />
                    </button>
                    <Drawer
                      anchor="right"
                      open={Boolean(openDrawer)}
                      onClose={toggleDrawer(false)}
                      ModalProps={{
                        keepMounted: false,
                      }}
                      sx={{ zIndex: 1 }}
                    >
                      <div className="right-drawer-component-small">
                        <TableRightDrawer
                          toggleDrawer={toggleDrawer}
                          fieldFilter={fieldFilter}
                          selectedFieldFilter={selectedFieldFilter}
                          handleSelectedFieldFilter={handleSelectedFieldFilter}
                          setDraggedFieldOrder={setDraggedFieldOrder}
                          draggedFieldOrder={draggedFieldOrder}
                        />
                      </div>
                    </Drawer>
                  </div>
                  <div
                    className={`po-table-wrapper po-table-scroll-container theme-scrollable-container-${themeColor}`}
                  >
                    <table>
                      <thead className="outward-delivery-table-head">
                        <tr>
                          <th aria-label="Select delivery">
                            <input
                              type="checkbox"
                              checked={isAllDeliveriesSelected}
                              onChange={handleSelectAllDeliveries}
                              aria-label="Select all deliveries"
                            />
                          </th>
                          {filteredFieldFilter.map((field) => (
                            <th key={field.value}>{field.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(deliveryLineitemData || []).map((deliveryObj) => {
                          const deliveryLineItems =
                            deliveryObj.line_items || [];
                          const tableRows = deliveryLineItems.length
                            ? deliveryLineItems
                            : [{}];
                          const rowSpan = tableRows.length;

                          return tableRows.map((item, itemIndex) => (
                            <tr
                              key={
                                item?.delivery_line_item_id ||
                                `${deliveryObj.delivery_id}-${itemIndex}`
                              }
                            >
                              {itemIndex === 0 && (
                                <td rowSpan={rowSpan}>
                                  <input
                                    type="checkbox"
                                    checked={selectedDeliveryOrder.some(
                                      (selectedItem) =>
                                        selectedItem.delivery_id ===
                                        deliveryObj.delivery_id,
                                    )}
                                    onChange={(e) =>
                                      handleDeliverySelection(e, deliveryObj)
                                    }
                                  />
                                </td>
                              )}
                              {filteredFieldFilter.map((field) => {
                                const isDeliveryLevelField =
                                  deliveryLevelFields.includes(field.key);

                                if (isDeliveryLevelField && itemIndex > 0) {
                                  return null;
                                }

                                return (
                                  <td
                                    key={field.value}
                                    rowSpan={
                                      isDeliveryLevelField ? rowSpan : undefined
                                    }
                                  >
                                    {renderDeliveryColumnValue(
                                      field.key,
                                      deliveryObj,
                                      item,
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ));
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            {formData?.showDeliveryComponent && (
              <div className="footer-container border-radius-4 py-2">
                <div className="div-space-between div-align-center footer-content-for-page">
                  <div className="div-flex-row cg-20 pagination-container div-align-center">
                    <div className="div-flex-row cg-10 div-align-center">
                      <button
                        className="icon-btn-none"
                        onClick={handlePreviousPage}
                        aria-label="Enter page number"
                        type="button"
                      >
                        <ArrowLeft className="icon-size-sm" />
                      </button>
                      <input
                        type="number"
                        className="page-number-container text-sm"
                        value={formattedPageNumber}
                        onChange={handleInputChange}
                        aria-label="Page number"
                        max={totalPages}
                        min="1"
                      />
                      <button
                        className="icon-btn-none"
                        onClick={handleNextPage}
                        aria-label="Enter page number"
                        type="button"
                      >
                        <ArrowRight className="icon-size-sm" />
                      </button>
                    </div>
                    <span className="text-grey text-sm">
                      Page {pageNumber} of {totalPages}
                    </span>
                  </div>
                  <div className="div-flex-row cg-10 div-align-center">
                    <input
                      type="checkbox"
                      name="autoLoadCreation"
                      checked={formData?.autoLoadCreation || false}
                      onChange={handleOnChange}
                    />
                    <span className="text-sm text-bold">
                      Create Load Automatically
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="empty-state-container">
            <img
              src={CreateDeliveryEmptyState}
              alt="Search PO to proceed"
              style={{
                maxWidth: '100%',
                width: '200px',
                height: 'auto',
              }}
            />
            <div className="margin-top-10">
              <span className="text-xxl text-bold">No delivery planned</span>
            </div>
            <div>
              <span className="text-md text-grey">
                To plan your delivery at the moment click on checkbox above.{' '}
                <br /> Add all the required delivery to add in this vehicle.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OutwardVehicleTrackingAttachDeliverySection;
